// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../components/ui/breadcrumb';
import {
  Upload,
  FolderOpen,
  BookOpen,
  Wrench,
  FolderPlus,
  ChevronRight,
  Home,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import UploadModal from '../components/UploadModal';
import FilePreviewModal from '../components/FilePreviewModal';
import PdfFormFiller from '../components/PdfFormFiller';
import CreateFolderModal from '../components/CreateFolderModal';
import { RenameModal, MoveModal } from '../components/MoveRenameModals';
import { SignDocumentModal, DocumentSignaturesModal } from '../components/SignDocumentModal';
import DocumentsToolbar from '../components/DocumentsToolbar';
import FolderRow from '../components/FolderRow';
import FileRow from '../components/FileRow';
import { useDocumentsSort } from '../hooks/useDocumentsSort';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AREA_CONFIG = {
  congregacao: {
    name: 'Congregação',
    fullName: 'Contas da Congregação',
    icon: BookOpen,
    color: 'text-congregation',
    bgColor: 'bg-congregation-soft',
    borderColor: 'border-congregation-border',
  },
  manutencao: {
    name: 'Manutenção',
    fullName: 'Contas da Manutenção',
    icon: Wrench,
    color: 'text-maintenance',
    bgColor: 'bg-maintenance-soft',
    borderColor: 'border-maintenance-border',
  },
};

export default function DocumentsPage() {
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const canWriteDocs = isAdmin() || ['servo_contas', 'secretario'].includes(user?.role);
  const [searchParams, setSearchParams] = useSearchParams();
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentFolderId, setCurrentFolderId] = useState(searchParams.get('folder') || null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [createFolderModalOpen, setCreateFolderModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  const [renameModal, setRenameModal] = useState({ open: false, item: null, type: null });
  const [moveModal, setMoveModal] = useState({ open: false, item: null, type: null });
  const [signModal, setSignModal] = useState({ open: false, file: null });
  const [signaturesModal, setSignaturesModal] = useState({ open: false, file: null, signatures: [] });

  const [formFillerFile, setFormFillerFile] = useState(null);

  // Selected area persists across navigation (UI preference, not a credential).
  const selectedArea = localStorage.getItem('selectedArea') || 'congregacao';
  const currentArea = AREA_CONFIG[selectedArea];

  const fetchData = useCallback(
    async (folderId = null, search = '') => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.append('category', selectedArea);
        if (folderId) params.append('folder_id', folderId);
        if (search) params.append('search', search);

        const response = await axios.get(`${API}/files?${params.toString()}`);
        setFiles(response.data.files);
        setFolders(response.data.folders);

        if (folderId) {
          const breadcrumbRes = await axios.get(`${API}/folders/${folderId}/breadcrumbs`);
          setBreadcrumbs(breadcrumbRes.data);
        } else {
          setBreadcrumbs([]);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Erro ao carregar dados');
      } finally {
        setLoading(false);
      }
    },
    [selectedArea]
  );

  const { sortBy, setSortBy, sortOrder, setSortOrder, sortedFolders, sortedFiles } =
    useDocumentsSort(folders, files);

  useEffect(() => {
    if (!localStorage.getItem('selectedArea')) {
      navigate('/select-area');
      return;
    }
    const folderId = searchParams.get('folder');
    setCurrentFolderId(folderId);
    fetchData(folderId, searchQuery);
    // searchQuery omitted on purpose: handleSearch already invokes fetchData on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, selectedArea, fetchData, navigate]);

  const handleFolderClick = (folderId) => {
    const newParams = new URLSearchParams(searchParams);
    if (folderId) newParams.set('folder', folderId);
    else newParams.delete('folder');
    setSearchParams(newParams);
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    fetchData(currentFolderId, query);
  };

  const handleDownload = async (file) => {
    try {
      const response = await axios.get(`${API}/files/${file.id}/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download iniciado');
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Erro ao baixar arquivo');
    }
  };

  const handleDeleteFile = async (file) => {
    if (!window.confirm(`Deseja excluir "${file.original_filename}"?`)) return;
    try {
      await axios.delete(`${API}/files/${file.id}`);
      toast.success('Arquivo excluído com sucesso');
      fetchData(currentFolderId, searchQuery);
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Erro ao excluir arquivo');
    }
  };

  const handleDeleteFolder = async (folder) => {
    try {
      await axios.delete(`${API}/folders/${folder.id}`);
      toast.success('Pasta excluída com sucesso');
      fetchData(currentFolderId, searchQuery);
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (detail.includes('arquivo(s)') || detail.includes('subpasta(s)')) {
        const confirmDelete = window.confirm(
          `A pasta "${folder.name}" não está vazia.\n\n${detail}\n\nDeseja excluir a pasta e TODO seu conteúdo? Esta ação não pode ser desfeita.`
        );
        if (confirmDelete) {
          try {
            await axios.delete(`${API}/folders/${folder.id}?force=true`);
            toast.success('Pasta e todo seu conteúdo excluídos com sucesso');
            fetchData(currentFolderId, searchQuery);
          } catch (forceError) {
            toast.error(forceError.response?.data?.detail || 'Erro ao excluir pasta');
          }
        }
      } else {
        toast.error(detail || 'Erro ao excluir pasta');
      }
    }
  };

  const handleViewSignatures = async (file) => {
    try {
      const response = await axios.get(`${API}/documents/${file.id}/signatures`);
      setSignaturesModal({ open: true, file, signatures: response.data.signatures });
    } catch (error) {
      toast.error('Erro ao carregar rubricas');
    }
  };

  const isAdminUser = isAdmin();
  const isEmpty = sortedFolders.length === 0 && sortedFiles.length === 0;

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 font-heading">
              Documentos
            </h1>
            <p className="text-slate-500 mt-1">{currentArea.fullName}</p>
          </div>
          {canWriteDocs && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => setCreateFolderModalOpen(true)}
              data-testid="create-folder-button"
              className="rounded-xl border-slate-200 hover:border-slate-300"
            >
              <FolderPlus className="w-4 h-4 mr-2" />
              Nova Pasta
            </Button>
            <Button
              onClick={() => setUploadModalOpen(true)}
              data-testid="upload-file-button-docs"
              className="bg-slate-900 hover:bg-slate-800 rounded-xl shadow-lg shadow-slate-900/20 active:scale-[0.98] transition-all"
            >
              <Upload className="w-4 h-4 mr-2" />
              Enviar
            </Button>
          </div>
          )}
        </div>

        {/* Breadcrumbs */}
        {breadcrumbs.length > 0 && (
          <div className="bg-white rounded-xl p-3 border border-slate-100 shadow-sm">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink
                    onClick={() => handleFolderClick(null)}
                    className="cursor-pointer flex items-center gap-1.5 text-slate-600 hover:text-slate-900 transition-colors"
                  >
                    <Home className="w-4 h-4" />
                    Raiz
                  </BreadcrumbLink>
                </BreadcrumbItem>
                {breadcrumbs.map((crumb, index) => (
                  <React.Fragment key={crumb.id}>
                    <BreadcrumbSeparator>
                      <ChevronRight className="w-4 h-4 text-slate-300" />
                    </BreadcrumbSeparator>
                    <BreadcrumbItem>
                      {index === breadcrumbs.length - 1 ? (
                        <BreadcrumbPage className="font-medium text-slate-900">
                          {crumb.name}
                        </BreadcrumbPage>
                      ) : (
                        <BreadcrumbLink
                          onClick={() => handleFolderClick(crumb.id)}
                          className="cursor-pointer text-slate-600 hover:text-slate-900 transition-colors"
                        >
                          {crumb.name}
                        </BreadcrumbLink>
                      )}
                    </BreadcrumbItem>
                  </React.Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        )}

        <DocumentsToolbar
          searchQuery={searchQuery}
          onSearchChange={handleSearch}
          sortBy={sortBy}
          sortOrder={sortOrder}
          setSortBy={setSortBy}
          setSortOrder={setSortOrder}
        />

        {/* Files & Folders Table */}
        <Card data-testid="files-table-card" className="border-0 shadow-card rounded-xl overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <RefreshCw className="w-8 h-8 text-slate-400 animate-spin" />
              </div>
            ) : isEmpty ? (
              <div className="py-20 text-center">
                <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
                  <FolderOpen className="w-10 h-10 text-slate-400" />
                </div>
                <p className="text-lg font-semibold text-slate-700 mb-2">Nenhum item encontrado</p>
                <p className="text-sm text-slate-500 mb-6">
                  {searchQuery ? 'Tente outra busca' : 'Envie seus arquivos ou crie uma pasta para começar'}
                </p>
                <div className="flex gap-3 justify-center">
                  <Button
                    variant="outline"
                    onClick={() => setCreateFolderModalOpen(true)}
                    className="rounded-xl"
                  >
                    <FolderPlus className="w-4 h-4 mr-2" />
                    Nova Pasta
                  </Button>
                  <Button
                    onClick={() => setUploadModalOpen(true)}
                    className="rounded-xl bg-slate-900 hover:bg-slate-800"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Enviar Arquivo
                  </Button>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent border-b border-slate-100">
                      <TableHead className="min-w-[200px] text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Nome
                      </TableHead>
                      <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">
                        Tamanho
                      </TableHead>
                      <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">
                        Data
                      </TableHead>
                      <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">
                        Enviado por
                      </TableHead>
                      <TableHead className="w-[90px] text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Ações
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedFolders.map((folder, index) => (
                      <FolderRow
                        key={`folder-${folder.id}`}
                        folder={folder}
                        index={index}
                        areaConfig={currentArea}
                        onOpen={handleFolderClick}
                        onRename={(f) => setRenameModal({ open: true, item: f, type: 'folder' })}
                        onMove={(f) => setMoveModal({ open: true, item: f, type: 'folder' })}
                        onDelete={handleDeleteFolder}
                        canWrite={canWriteDocs}
                      />
                    ))}

                    {sortedFiles.map((file, index) => (
                      <FileRow
                        key={`file-${file.id}`}
                        file={file}
                        index={index}
                        totalFolders={sortedFolders.length}
                        areaConfig={currentArea}
                        isAdminUser={isAdminUser}
                        onPreview={setPreviewFile}
                        onDownload={handleDownload}
                        onRename={(f) => setRenameModal({ open: true, item: f, type: 'file' })}
                        onMove={(f) => setMoveModal({ open: true, item: f, type: 'file' })}
                        onDelete={handleDeleteFile}
                        onSign={(f) => setSignModal({ open: true, file: f })}
                        onViewSignatures={handleViewSignatures}
                        canWrite={canWriteDocs}
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <UploadModal
        open={uploadModalOpen}
        onOpenChange={setUploadModalOpen}
        onSuccess={() => fetchData(currentFolderId, searchQuery)}
        defaultCategory={selectedArea}
        currentFolderId={currentFolderId}
        fixedCategory={true}
      />

      <CreateFolderModal
        open={createFolderModalOpen}
        onOpenChange={setCreateFolderModalOpen}
        onSuccess={() => fetchData(currentFolderId, searchQuery)}
        category={selectedArea}
        parentId={currentFolderId}
      />

      <FilePreviewModal
        file={previewFile}
        open={!!previewFile}
        onOpenChange={(open) => !open && setPreviewFile(null)}
        onDownload={handleDownload}
        onFillForm={(file) => setFormFillerFile(file)}
      />

      <PdfFormFiller
        file={formFillerFile}
        open={!!formFillerFile}
        onOpenChange={(open) => !open && setFormFillerFile(null)}
        onSaved={() => {
          fetchData(currentFolderId, searchQuery);
          toast.success('Formulário preenchido e salvo com sucesso!');
        }}
      />

      <RenameModal
        isOpen={renameModal.open}
        onClose={() => setRenameModal({ open: false, item: null, type: null })}
        item={renameModal.item}
        itemType={renameModal.type}
        onSuccess={() => {
          fetchData(currentFolderId, searchQuery);
          setRenameModal({ open: false, item: null, type: null });
        }}
      />

      <MoveModal
        isOpen={moveModal.open}
        onClose={() => setMoveModal({ open: false, item: null, type: null })}
        item={moveModal.item}
        itemType={moveModal.type}
        category={selectedArea}
        onSuccess={() => {
          fetchData(currentFolderId, searchQuery);
          setMoveModal({ open: false, item: null, type: null });
        }}
      />

      <SignDocumentModal
        open={signModal.open}
        onOpenChange={(open) => !open && setSignModal({ open: false, file: null })}
        file={signModal.file}
        onSuccess={() => fetchData(currentFolderId, searchQuery)}
      />

      <DocumentSignaturesModal
        open={signaturesModal.open}
        onOpenChange={(open) =>
          !open && setSignaturesModal({ open: false, file: null, signatures: [] })
        }
        file={signaturesModal.file}
        signatures={signaturesModal.signatures}
      />
    </Layout>
  );
}
